import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import * as repository from "@/data/authRepository";
import { api, isMockApi } from "@/lib/api";
import { onSessionExpired, serverAuth } from "@/lib/authSession";

const AuthContext = createContext(null);

/**
 * Restaurant login is owner-only. The employee value remains null for older
 * components that are being retired alongside the team feature.
 */
export function AuthProvider({ children }) {
  const { createVendor, getSession, getVendorByEmail, getVendorById, saveVendor, setSession, clearSession, seedIfNeeded } = repository;
  const [vendor, setVendor] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [role, setRole] = useState(null); // "owner" | null
  const [loading, setLoading] = useState(true);

  const hydrate = useCallback(() => {
    const s = getSession();
    if (!s) {
      setVendor(null); setEmployee(null); setRole(null);
      return;
    }
    if (s.vendorId && s.role === "owner") {
      const v = getVendorById(s.vendorId);
      if (v) { setVendor(v); setEmployee(null); setRole("owner"); return; }
    }
    clearSession();
    setVendor(null); setEmployee(null); setRole(null);
  }, []);

  const applyServerSession = useCallback((next) => {
    setVendor(next?.vendor || null);
    setEmployee(next?.employee || null);
    setRole(next?.role || null);
    return next;
  }, []);

  useEffect(() => {
    let active = true;
    async function restore() {
      if (isMockApi) {
        seedIfNeeded();
        hydrate();
      } else {
        const next = await serverAuth.restoreVendor();
        if (active) applyServerSession(next);
      }
      if (active) setLoading(false);
    }
    restore();
    return () => { active = false; };
  }, [applyServerSession, hydrate]);

  /**
   * Resetting the password — here or on another device — retires this token.
   * The adapter has already dropped it; this is what turns that into a visible
   * sign-out rather than a dashboard whose every request quietly fails.
   */
  useEffect(() => {
    if (isMockApi) return undefined;
    return onSessionExpired("vendor", () => {
      setVendor(null); setEmployee(null); setRole(null);
      toast.info("Your password was changed. Please sign in again.");
    });
  }, []);

  const login = useCallback(async (email, password) => {
    if (!isMockApi) {
      const next = await serverAuth.loginVendor({ email, password });
      // Login returns a lean identity; hydrate `/auth/me` before rendering the
      // vendor dashboard so catalogue and storefront state come from the API.
      return applyServerSession((await serverAuth.restoreVendor()) || next);
    }
    // Try vendor owner first
    const v = getVendorByEmail(email);
    if (v && v.password === password) {
      setSession({ email: v.email, vendorId: v.id, role: "owner" });
      setVendor(v); setEmployee(null); setRole("owner");
      return { vendor: v, employee: null, role: "owner" };
    }
    if (!v) throw new Error("No account found with that email.");
    throw new Error("Incorrect password.");
  }, [applyServerSession]);

  const register = useCallback(async (email, password) => {
    if (!isMockApi) {
      const next = await serverAuth.registerVendor({ email, password, businessName: email.split("@")[0] });
      return applyServerSession(next);
    }
    const v = createVendor({ email, password });
    setSession({ email: v.email, vendorId: v.id, role: "owner" });
    setVendor(v); setEmployee(null); setRole("owner");
    return v;
  }, [applyServerSession]);

  const logout = useCallback(async () => {
    if (!isMockApi) await serverAuth.logoutVendor();
    clearSession();
    setVendor(null); setEmployee(null); setRole(null);
  }, []);

  const refresh = useCallback(async () => {
    if (isMockApi) return hydrate();
    return applyServerSession(await serverAuth.restoreVendor());
  }, [applyServerSession, hydrate]);

  const updateVendor = useCallback((patch) => {
    setVendor((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...patch };
      // The onboarding/resource API is migrated separately. Do not write a
      // server-authenticated restaurant into the mock repository.
      if (isMockApi) saveVendor(next);
      return next;
    });
  }, []);

  const completeOnboarding = useCallback(async (form) => {
    if (isMockApi) return updateVendor(form);
    await api.updateOwnStorefront({
      name: form.name, slug: form.slug, tagline: form.tagline || undefined,
      description: form.description || undefined, logoUrl: form.logo || undefined,
      coverUrl: form.coverImage || undefined, address: form.address || undefined,
      phone: form.phone || undefined, isPublished: true,
    });
    await api.updateOwnRestaurant({
      name: form.name, address: form.address || undefined, phone: form.phone || undefined, onboarded: true,
    });
    return applyServerSession(await serverAuth.restoreVendor());
  }, [applyServerSession, updateVendor]);

  const isOwner = role === "owner";
  const isEmployee = role === "employee";

  return (
    <AuthContext.Provider value={{ vendor, employee, role, isOwner, isEmployee, loading, login, register, logout, refresh, updateVendor, completeOnboarding }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
